<template>
  <div
    v-if="canProject"
    class="level-item action"
    :class="{
      active: isProjectionTypePlane,
      disabled: !hasSelection,
    }"
    @click="setProjectionType(ProjectionType.plane)"
  >
    <el-tooltip :content="$t('components.mesh-editor.tooltip.projectionPlane')" placement="bottom">
      <font-awesome-icon icon="square" />
    </el-tooltip>
  </div>
  <div
    v-if="canProject"
    class="level-item action"
    :class="{
      active: isProjectionTypeCylinder,
      disabled: !hasSelection,
    }"
    @click="setProjectionType(ProjectionType.cylinder)"
  >
    <el-tooltip :content="$t('components.mesh-editor.tooltip.projectionCylinder')" placement="bottom">
      <font-awesome-icon icon="database" />
    </el-tooltip>
  </div>
</template>

<script lang="ts">
import * as THREE from 'three';
import { RendererPublicInterface } from 'troisjs';
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';

import TransformTools from '@/components/three/tools/TransformTools.vue';
import { EmbossingExportData } from '@/services/api/templateService';
import { ProjectionCategory } from '@/types/enum/editor';
import * as THREEEnum from '@/types/enum/three';
import { SpecialObjectName } from '@/types/enum/three';
import { EditorMode } from '@/types/ui/EditorMode';
import { HistoryInfo, HistoryList, HistoryOperationType } from '@/types/ui/HistoryList';
import { MeshTreeData, MeshTreeDataItem } from '@/types/ui/MeshTreeData';
import { SelectionList } from '@/types/ui/SelectionList';
import { ViewCtrl } from '@/types/ui/ViewCtrl';
import * as THREEBoundingBox from '@/utils/three/boundingBox';
import * as THREEMaterial from '@/utils/three/material';
import { EmbossingData, ProjectionSidebarData } from '@/types/interfaces/projectionSidebarData';

interface ProjectionData extends ProjectionSidebarData {
  projectionType: THREEEnum.ProjectionType;
  position: THREE.Vector3 | null;
  rotation: THREE.Euler;
  scale: THREE.Vector3;
}

@Component({
  components: { TransformTools },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/

export default class ProjectionTools extends Vue implements HistoryInfo {
  //#region properties
  @Prop() modelValue!: MeshTreeData;
  @Prop() troisRenderer!: RendererPublicInterface;
  @Prop() editorMode!: EditorMode;
  @Prop() selectionList!: SelectionList;
  @Prop({ default: false }) hasSelection!: boolean;
  @Prop() viewCtrl!: ViewCtrl;
  @Prop({ default: true }) canCurve!: boolean;
  @Prop() readonly selectObject!: (target: THREE.Object3D | null, setSelectionFlag: boolean) => Promise<void>;
  @Prop({ default: true }) canProject!: boolean;
  @Prop({
    default: {
      scale: 1,
      embossingFile: null,
      color: '#ffffff',
    },
  })
  embossing!: EmbossingData;
  @Prop() historyList!: HistoryList;

  //history
  lastUpdateTime = -1;

  // transform
  transformTools: InstanceType<typeof TransformTools> | null = null;
  projectionReference!: THREE.Mesh;

  //projection
  activeProjectionType: THREEEnum.ProjectionType = THREEEnum.ProjectionType.cylinder;
  isProjectionTypeCylinder = true;
  isProjectionTypePlane = false;

  //data
  projectionDataPerMesh: {
    [name: string]: EmbossingData;
  } = {};

  // enums for use in template section
  ProjectionType = THREEEnum.ProjectionType;
  //#endregion properties

  //#region load / unload
  @Watch('lastUpdateTime', { immediate: true })
  onDataChanged(): void {
    if (this.lastUpdateTime > -1) {
      const mesh = this.selectionList.getSelectedObjectSaveId();
      if (mesh) this.modelValue.updateDB(mesh);
    }
  }
  //#endregion load / unload

  //#region projection
  @Watch('embossing', { immediate: true, deep: true })
  async onEmbossingChanged(restoreAndSave = true): Promise<void> {
    //todo: fix performance - too many calls (deep: true)
    if (this.projectionReference) {
      const selection = this.selectionList.getSelectedObject();
      const embossing = this.getActiveEmbossing(this.embossing);
      if (embossing) {
        if (selection && embossing.color) THREEMaterial.setColor(selection, embossing.color);
        THREEMaterial.setTextureFile(this.projectionReference, embossing.embossingFile);
        //if (selection) await THREEMaterial.setBumpTextureFile(selection, embossing.embossingFile);
        if (selection) THREEMaterial.setProjectionBumpMapping(selection, embossing.embossingFile, THREEMaterial.getColor(selection), '#ffffff');
        this.updateProjectionProportions();
      }
      if (restoreAndSave) {
        this.saveData(false);
        if (this.canProject) this.createProjectionReferenceForSelectedObject();
      }
    }
  }

  @Watch('canProject', { immediate: true })
  onCanProjectionChanged(): void {
    if (this.canProject) this.createProjectionReferenceForSelectedObject();
    if (this.projectionReference) this.projectionReference.visible = this.canProject;
  }

  changeTransform(): void {
    this.updateProjectionProportions();
    this.saveData();
  }

  updateProjectionProportions(): void {
    const embossing = this.getActiveEmbossing(this.embossing);
    const selection = this.selectionList.getSelectedObject();
    if (embossing && this.projectionReference && selection) {
      const getVertexList = (vertexList: THREE.BufferAttribute | THREE.InterleavedBufferAttribute): THREE.Vector3[] => {
        const vertex = new THREE.Vector3();
        const vertexArray: THREE.Vector3[] = [];
        for (let i = 0; i < vertexList.count; i++) {
          vertex.fromBufferAttribute(vertexList, i);
          vertexArray.push(vertex.clone());
        }
        return vertexArray;
      };
      const selectionMesh = selection as THREE.Mesh;
      const vertexArraySelection = getVertexList(selectionMesh.geometry.getAttribute('position'));
      const vertexArrayReference = getVertexList(this.projectionReference.geometry.getAttribute('position')).map((item) =>
        this.projectionReference.localToWorld(item)
      );
      let uvList = selectionMesh.geometry.getAttribute('uv');
      if (!uvList) {
        selectionMesh.geometry.setAttribute('uv', new THREE.Float32BufferAttribute(Array(vertexArraySelection.length * 2).fill(0), 2));
        uvList = selectionMesh.geometry.getAttribute('uv');
      }
      let uv2List = selectionMesh.geometry.getAttribute('uv2');
      if (!uv2List) {
        selectionMesh.geometry.setAttribute('uv2', new THREE.Float32BufferAttribute(Array(vertexArraySelection.length * 2).fill(0), 2));
        uv2List = selectionMesh.geometry.getAttribute('uv2');
      }
      let ignoreList = selectionMesh.geometry.getAttribute('ignore');
      if (!ignoreList) {
        selectionMesh.geometry.setAttribute('ignore', new THREE.Float32BufferAttribute(Array(vertexArraySelection.length).fill(1), 1));
        ignoreList = selectionMesh.geometry.getAttribute('ignore');
      }
      let directionList = selectionMesh.geometry.getAttribute('direction');
      if (!directionList) {
        selectionMesh.geometry.setAttribute('direction', new THREE.Float32BufferAttribute(Array(vertexArraySelection.length).fill(1), 1));
        directionList = selectionMesh.geometry.getAttribute('direction');
      }
      const normal = this.projectionReference
        .localToWorld(new THREE.Vector3(0, 1, 0))
        .sub(this.projectionReference.localToWorld(new THREE.Vector3(0, 0, 0)))
        .normalize();
      const refPlane = new THREE.Plane(normal);
      const center = selectionMesh.localToWorld(this.projectionReference.position.clone());
      const size = vertexArrayReference[0]
        .clone()
        .sub(vertexArrayReference[vertexArrayReference.length / 2])
        .length();
      const raycaster = new THREE.Raycaster();
      if (this.isProjectionTypeCylinder) {
        const stretchY = this.projectionReference.clone(true);
        this.projectionReference.parent?.add(stretchY);
        stretchY.scale.y *= 2;
        stretchY.updateWorldMatrix(false, false);
        stretchY.visible = false;
        for (let i = 0; i < uvList.count; i++) {
          const vertex = selectionMesh.localToWorld(vertexArraySelection[i]);
          const layerCenter = center.clone();
          const distance = refPlane.distanceToPoint(vertexArraySelection[i]) - size / 2;
          layerCenter.add(normal.clone().multiplyScalar(distance));
          const direction = vertex.clone().sub(layerCenter).normalize();
          raycaster.set(layerCenter, direction);
          const intersection = raycaster.intersectObject(this.projectionReference, false);
          const uv = intersection[0]?.uv;
          if (uv) {
            uvList.setXY(i, uv.x, uv.y);
            const min = 0.05;
            const max = 0.95;
            uv2List.setXY(i, uv.x < min || uv.x > max ? 1 - uv.x : uv.x, uv.y);
            ignoreList.setX(i, uv.x < min || uv.x > max ? -1 : 0);
            directionList.setX(i, uv.x < min || uv.x > max ? 0 : uv.x > 0.5 ? -1 : 1);
          } else {
            let minIndex = 0;
            let minDistance = vertex.clone().sub(vertexArrayReference[0]).length();
            for (let j = 1; j < vertexArrayReference.length; j++) {
              const distance = vertex.clone().sub(vertexArrayReference[j]).length();
              if (distance < minDistance) {
                minDistance = distance;
                minIndex = j;
              }
            }
            let x = minIndex % 2;
            const y = minIndex < vertexArrayReference.length / 2 ? 1 : 0;
            const intersectionTest = raycaster.intersectObject(stretchY, false);
            if (intersectionTest[0]?.uv) x = intersectionTest[0].uv.x;
            uvList.setXY(i, x, y);
            uv2List.setXY(i, x, y);
            ignoreList.setX(i, 1);
          }
        }
      } else {
        const normalArray = getVertexList(selectionMesh.geometry.getAttribute('normal'));
        const direction = this.projectionReference.getWorldDirection(new THREE.Vector3(0, 0, 1)).normalize();
        const direction2 = direction.clone().negate();
        const stretchX = this.projectionReference.clone(true);
        const stretchY = this.projectionReference.clone(true);
        this.projectionReference.parent?.add(stretchX);
        this.projectionReference.parent?.add(stretchY);
        stretchX.scale.x *= 2;
        stretchX.updateWorldMatrix(false, false);
        stretchX.visible = false;
        stretchY.scale.y *= 2;
        stretchY.updateWorldMatrix(false, false);
        stretchY.visible = false;
        for (let i = 0; i < uvList.count; i++) {
          const vertex = selectionMesh.localToWorld(vertexArraySelection[i]);
          const normal = normalArray[i];
          const normalDirection = normal.clone().add(direction).length();
          raycaster.set(vertex, normalDirection > 1 ? direction : direction2);
          const intersection = raycaster.intersectObject(this.projectionReference, false);
          const uv = intersection[0]?.uv;
          if (uv) {
            uvList.setXY(i, uv.x, uv.y);
            uv2List.setXY(i, uv.x, uv.y);
            ignoreList.setX(i, 0);
          } else {
            let minIndex = 0;
            let minDistance = vertex.clone().sub(vertexArrayReference[0]).length();
            for (let j = 1; j < vertexArrayReference.length; j++) {
              const distance = vertex.clone().sub(vertexArrayReference[j]).length();
              if (distance < minDistance) {
                minDistance = distance;
                minIndex = j;
              }
            }
            let x = minIndex % 2;
            let y = minIndex < vertexArrayReference.length / 2 ? 1 : 0;
            let intersectionTest = raycaster.intersectObject(stretchX, false);
            if (intersectionTest[0]?.uv) y = intersectionTest[0].uv.y;
            else {
              intersectionTest = raycaster.intersectObject(stretchY, false);
              if (intersectionTest[0]?.uv) x = intersectionTest[0].uv.x;
            }
            uvList.setXY(i, x, y);
            uv2List.setXY(i, x, y);
            ignoreList.setX(i, 1);
          }
        }
        this.projectionReference.parent?.remove(stretchX);
        this.projectionReference.parent?.remove(stretchY);
      }
      uvList.needsUpdate = true;
      uv2List.needsUpdate = true;
      ignoreList.needsUpdate = true;
      directionList.needsUpdate = true;
      THREEMaterial.scaleTexture(
        this.projectionReference,
        embossing.scaleFactor,
        embossing.scaleFactor,
        embossing.offset.x,
        embossing.offset.y,
        this.getProjectionProportions()
      );
      THREEMaterial.scaleTexture(
        selection,
        embossing.scaleFactor,
        embossing.scaleFactor,
        embossing.offset.x,
        embossing.offset.y,
        this.getProjectionProportions()
      );
    }
  }

  getProjectionProportions(): number {
    if (this.projectionReference) {
      const box = THREEBoundingBox.getBoundingBox([this.projectionReference], true, false);
      const x = (box.max.x - box.min.x) * this.projectionReference.scale.x;
      const y = (box.max.y - box.min.y) * this.projectionReference.scale.y;
      //const z = (box.max.z - box.min.z) * this.projectionReference.scale.z;
      if (this.isProjectionTypePlane) {
        return x / y;
      } else {
        return (x * Math.PI) / y;
      }
    }
    return 1;
  }

  setProjectionType(type: THREEEnum.ProjectionType, restoreAndSave = true): void {
    if (this.activeProjectionType !== type) {
      this.activeProjectionType = type;
      this.isProjectionTypeCylinder = type === THREEEnum.ProjectionType.cylinder;
      this.isProjectionTypePlane = type === THREEEnum.ProjectionType.plane;
      if (restoreAndSave) {
        this.saveData();
        if (this.canProject) {
          this.createProjectionReferenceForSelectedObject();
          const meshId = this.selectionList.getSelectedObjectUuid();
          if (meshId) {
            const oldType = this.isProjectionTypeCylinder ? THREEEnum.ProjectionType.plane : THREEEnum.ProjectionType.cylinder;
            const mesh = this.selectionList.getSelectedObject();
            if (mesh) {
              this.historyList.add(
                meshId,
                HistoryOperationType.embossing,
                async () => {
                  this.undoRedoProjectionType(mesh, oldType);
                },
                async () => {
                  this.undoRedoProjectionType(mesh, type);
                }
              );
            }
          }
        }
      }
    }
  }

  undoRedoProjectionType(object: THREE.Object3D, type: THREEEnum.ProjectionType): void {
    this.selectObject(object, true).then(() => {
      if (this.activeProjectionType !== type) {
        this.activeProjectionType = type;
        this.isProjectionTypeCylinder = type === THREEEnum.ProjectionType.cylinder;
        this.isProjectionTypePlane = type === THREEEnum.ProjectionType.plane;
        this.saveData();
        if (this.canProject) {
          this.createProjectionReferenceForObject([object]);
        }
      }
    });
  }

  private getActiveProjection(): ProjectionData | null {
    const selection = this.selectionList.getSelectedObjectSaveId();
    if (selection) {
      const data = this.projectionDataPerMesh[selection];
      return this.getActiveEmbossing(data);
    }
    return null;
  }

  getActiveEmbossing(data: EmbossingData): ProjectionData | null {
    if (data) {
      const activeCategory = data.activeCategory;
      const index = data.projection.findIndex((item) => item.projectionCategory === activeCategory);
      if (index > -1) {
        return data.projection[index] as ProjectionData;
      }
    }
    return null;
  }
  //#endregion projection

  //#region create / save
  private saveData(saveAll = true): void {
    const selection = this.selectionList.getSelectedObjectSaveId();
    const embossing = this.getActiveEmbossing(this.embossing);
    if (selection && this.projectionReference && embossing) {
      const projection: ProjectionData = {
        projectionType: this.activeProjectionType,
        position: this.projectionReference.position.clone(),
        rotation: this.projectionReference.rotation.clone(),
        scale: this.projectionReference.scale.clone(),
        projectionCategory: embossing.projectionCategory,
        aspectRatio: this.getProjectionProportions(),
        scaleFactor: embossing.scaleFactor,
        offset: { x: embossing.offset.x, y: embossing.offset.y },
        embossingDefinition: embossing.embossingDefinition,
        colorDefinition: embossing.colorDefinition,
        embossingFile: embossing.embossingFile,
        color: embossing.color,
      };
      this.saveItem(selection, projection, saveAll);
      this.lastUpdateTime = Date.now();
    }
  }

  private saveItem(meshId: string, projection: ProjectionData, saveAll = true): void {
    if (!this.projectionDataPerMesh[meshId]) {
      this.projectionDataPerMesh[meshId] = {
        id: meshId,
        activeCategory: projection.projectionCategory,
        projection: [projection],
      };
    } else {
      const dbItem = this.projectionDataPerMesh[meshId];
      dbItem.activeCategory = projection.projectionCategory;
      const activeCategory = dbItem.activeCategory;
      const index = dbItem.projection.findIndex((item) => item.projectionCategory === activeCategory);
      if (index > -1) {
        if (saveAll) dbItem.projection[index] = projection;
        else {
          dbItem.projection[index].projectionCategory = projection.projectionCategory;
          dbItem.projection[index].scaleFactor = projection.scaleFactor;
          dbItem.projection[index].offset.x = projection.offset.x;
          dbItem.projection[index].offset.y = projection.offset.y;
          dbItem.projection[index].embossingDefinition = projection.embossingDefinition;
          dbItem.projection[index].colorDefinition = projection.colorDefinition;
          dbItem.projection[index].embossingFile = projection.embossingFile;
          dbItem.projection[index].color = projection.color;
        }
      } else {
        dbItem.projection.push(projection);
      }
    }
  }

  createProjectionReferenceForSelectedObject(restoreAndSave = true): void {
    const selection = this.selectionList.getSelectedObjects();
    this.createProjectionReferenceForObject(selection, restoreAndSave);
  }

  createProjectionReferenceForObject(objects: THREE.Object3D[], restoreAndSave = true): void {
    if (this.canProject) {
      if (this.projectionReference) {
        this.projectionReference.visible = false;
        if (this.transformTools) this.transformTools.setTransformControlsForTarget(null);
      }
      if (objects.length > 0) {
        const apiData = (objects[0] as any).apiData as MeshTreeDataItem;
        if (!apiData || apiData.isSpecialObject()) return;
        const objectId = (objects[0] as any).apiData.id;
        if (restoreAndSave) {
          this.restoreData();
        }
        const boundingBox = THREEBoundingBox.getBoundingBox(objects);
        const boxSize = boundingBox.getSize(new THREE.Vector3());
        const boxCenter = boundingBox.getCenter(new THREE.Vector3());
        const height = boundingBox.max.y - boundingBox.min.y;
        const path = new THREE.LineCurve3(new THREE.Vector3(0, -height / 2, 0), new THREE.Vector3(0, height / 2, 0));
        const geometry = this.isProjectionTypeCylinder
          ? new THREE.TubeGeometry(path, 1, Math.max(boxSize.x, boxSize.z) / 2, 20, false)
          : new THREE.PlaneGeometry(boxSize.x, boxSize.y);

        const uvs = geometry.attributes.uv as THREE.Float32BufferAttribute;
        if (this.isProjectionTypeCylinder) {
          const uvCoordsOriginal = [...(uvs.array as unknown as number[])];
          const uvCoordsNew = [...uvCoordsOriginal];
          for (let i = 0; i < uvs.count; i++) {
            uvCoordsNew[i * 2] = uvCoordsOriginal[i * 2 + 1];
            uvCoordsNew[i * 2 + 1] = uvCoordsOriginal[i * 2];
          }
          uvs.set(uvCoordsNew);
          uvs.needsUpdate = true;
        }
        if (!this.projectionReference) {
          const material = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            side: THREE.DoubleSide,
            opacity: 50 / 100.0,
            transparent: true,
          });
          this.projectionReference = new THREE.Mesh(geometry, material);
          this.projectionReference.name = SpecialObjectName.ProjectionPlane;
          this.onEmbossingChanged(false);
        } else {
          this.projectionReference.geometry = geometry;
        }
        this.projectionReference.uuid = objectId;
        this.projectionReference.visible = true;

        if (this.transformTools) {
          this.transformTools.setTransformControlsForTarget(this.projectionReference, false);
        }
        objects[0].add(this.projectionReference);
        this.projectionReference.position.copy(boxCenter.clone());
        this.projectionReference.rotation.set(0, 0, 0);
        this.projectionReference.scale.set(1, 1, 1);
        if (restoreAndSave) {
          if (this.projectionDataPerMesh[objectId]) {
            this.restoreData();
            const index = this.embossing.projection.findIndex((item) => item.projectionCategory === this.embossing.activeCategory);
            if (index > -1) {
              this.embossing.projection[index].aspectRatio = this.getProjectionProportions();
            }
          } else {
            this.saveData();
          }
        }
        this.updateProjectionProportions();
      }
    }
  }

  private createEmptyData(): void {
    const selectionId = this.selectionList.getSelectedObjectSaveId();
    if (selectionId) {
      const selection = this.selectionList.getSelectedObject();
      const data = this.projectionDataPerMesh[selectionId];
      const embossing = this.getActiveEmbossing(this.embossing);
      if (!data && embossing) {
        const projection: ProjectionData = {
          projectionType: this.activeProjectionType,
          position: null,
          rotation: new THREE.Euler(0, 0, 0),
          scale: new THREE.Vector3(1, 1, 1),
          projectionCategory: ProjectionCategory.custom,
          aspectRatio: 1,
          scaleFactor: 1,
          offset: { x: 0, y: 0 },
          embossingDefinition: [],
          colorDefinition: {},
          embossingFile: null,
          color: selection ? THREEMaterial.getColor(selection) : null,
        };
        this.projectionDataPerMesh[selectionId] = {
          id: selectionId,
          activeCategory: ProjectionCategory.custom,
          projection: [projection],
        };
        this.embossing.projection = [projection];
        this.embossing.activeCategory = ProjectionCategory.custom;
      }
    }
  }

  private restoreData(): void {
    this.createEmptyData();
    const selection = this.selectionList.getSelectedObjectSaveId();
    if (selection) {
      const meshData = this.projectionDataPerMesh[selection];
      if (meshData) {
        let hasChanges = this.embossing.projection.length !== meshData.projection.length;
        if (!hasChanges) {
          for (let i = 0; i < this.embossing.projection.length; i++) {
            hasChanges = this.embossing.projection[i].projectionCategory !== meshData.projection[i].projectionCategory;
            if (hasChanges) break;
          }
        }
        if (hasChanges) this.embossing.projection = [...meshData.projection];
        if (this.embossing.activeCategory !== meshData.activeCategory) this.embossing.activeCategory = meshData.activeCategory;
      }
      const data = this.getActiveProjection();
      if (data) {
        this.setProjectionType(data.projectionType, false);
        if (this.projectionReference) {
          if (data.position) this.projectionReference.position.copy(data.position.clone());
          this.projectionReference.rotation.copy(data.rotation.clone());
          this.projectionReference.scale.copy(data.scale.clone());
        }
        if (this.embossing) {
          if (this.embossing.id !== meshData.id) this.embossing.id = meshData.id;
          const embossing = this.getActiveEmbossing(this.embossing);
          if (embossing) {
            if (embossing.scaleFactor !== data.scaleFactor) embossing.scaleFactor = data.scaleFactor;
            if (embossing.offset.x !== data.offset.x) embossing.offset.x = data.offset.x;
            if (embossing.offset.y !== data.offset.y) embossing.offset.y = data.offset.y;
            if (embossing.embossingDefinition !== data.embossingDefinition) embossing.embossingDefinition = data.embossingDefinition;
            if (embossing.colorDefinition !== data.colorDefinition) embossing.colorDefinition = data.colorDefinition;
            if (embossing.embossingFile !== data.embossingFile) embossing.embossingFile = data.embossingFile;
            if (embossing.color !== data.color) embossing.color = data.color;
          }
        }
      }
    }
  }
  //#endregion create / save

  //#region import / export
  exportEmbossingForMeshId(meshId: string): EmbossingExportData[] {
    const data = this.projectionDataPerMesh[meshId];
    if (data) {
      return data.projection.map((item) => {
        const projection = item as ProjectionData;
        return {
          projectionType: projection.projectionType,
          position: projection.position,
          rotation: projection.rotation,
          scale: projection.scale,
          projection: item,
        };
      });
    }
    return [];
  }

  importEmbossingForMeshId(meshId: string, embossingList: EmbossingExportData[]): void {
    for (const item of embossingList) {
      const projection: ProjectionData = {
        projectionType: item.projectionType,
        position: item.position ? item.position.clone() : null,
        rotation: item.rotation.clone(),
        scale: item.scale.clone(),
        projectionCategory: item.projection.projectionCategory,
        aspectRatio: item.projection.aspectRatio,
        scaleFactor: item.projection.scaleFactor,
        offset: { x: item.projection.offset.x, y: item.projection.offset.y },
        embossingDefinition: item.projection.embossingDefinition,
        colorDefinition: item.projection.colorDefinition,
        embossingFile: item.projection.embossingFile,
        color: item.projection.color,
      };
      this.saveItem(meshId, projection);
    }
  }
  //#endregion import / export
}
</script>

<style lang="scss" scoped>
.active {
  color: var(--el-color-primary);
}

.level-item:not(:last-child) {
  display: flex;
  align-items: center;
  margin-right: 0.6rem;
  margin-bottom: 0;
}
</style>
